// A page's code that fails to load after a deploy: say so and reload past the
// cache, instead of leaving a blank page (2026-11-02).
//
// A script the browser asked for while a deploy spread could come back as the
// app's HTML, cached for a year (public/404.html and functions/utils/staticMiss.ts
// stop the server from doing that now; a browser that cached it before keeps
// it). A normal reload, even Ctrl+Shift+R, served that cached HTML again; only
// asking for the script itself with `cache: 'reload'` replaced it. So the reload
// here asks again for every script that failed, then reloads the page.

/** The scripts that failed to load on this page, as the browser named them. */
const failed = new Set<string>();

const SCRIPT_URL = /https?:\/\/[^\s'"()]+?\.(?:js|mjs|css)\b/g;

/** Whether an error is a page's code failing to load (each browser words it its own way). */
export function isChunkLoadError(e: unknown): boolean {
  const msg = e instanceof Error ? `${e.name} ${e.message}` : String(e);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|module script but the server responded with a MIME type|ChunkLoadError/i.test(msg);
}

/** Remembers the script addresses an error names, for `reloadFresh`. */
export function noteFailedScripts(e: unknown): void {
  const msg = e instanceof Error ? e.message : String(e);
  for (const m of msg.matchAll(SCRIPT_URL)) failed.add(m[0]);
}

/** Every script address noted so far (for tests). */
export function failedScripts(): string[] {
  return [...failed];
}

/** Asks again for every script that failed, past the cache, then reloads the page. */
export async function reloadFresh(fetcher: typeof fetch = fetch, reload: () => void = () => window.location.reload()): Promise<void> {
  await Promise.all([...failed].map((u) => fetcher(u, { cache: 'reload' }).catch(() => undefined)));
  reload();
}

/** Vite reports a script it failed to preload before the import itself fails; note it. */
export function watchPreloadErrors(target: Pick<Window, 'addEventListener'> = window): void {
  target.addEventListener('vite:preloadError', (e) => noteFailedScripts((e as Event & { payload?: unknown }).payload));
}
